/**
 * TerminalLog — backend console panel под orbit.
 *
 * Все события приходят с сервера через SSE (`useDashboardFeed`).
 * Backend держит единый ring buffer на 200 событий и пушит новые
 * каждые 3.8–6.8 с. Все подключённые клиенты видят ОДНУ И ТУ ЖЕ
 * последовательность с одинаковыми timestamps — два юзера могут
 * сравнить скриншоты и увидеть идентичные строки.
 *
 * Время — Europe/Moscow, 24-ч формат "18:05:32".
 *
 * UI:
 *   - Compact panel (4 строки) — что видно на дашборде. Glass-стиль
 *     приложения, фиксированные колонки timestamp/level/text.
 *   - Tap → modal в "хакерском" terminal-стиле: тёмный фон, 3 точки
 *     macOS-сверху, моноширинная сетка, мигающий курсор в конце,
 *     typewriter-эффект для самой свежей строки. Гибрид: рамка
 *     glass, наполнение — настоящая консоль.
 *   - Haptic-фидбек в открытой модалке на важные строки
 *     (WARN/ERR/[NET] auto-claim) — лёгкая микровибрация.
 */
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useDashboardFeed, type FeedEntry } from '@/hooks/useDashboardFeed';
import { useUISounds } from '@/hooks/useUISounds';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';

type LogLevel = FeedEntry['level'];

// ────────────────────────────────────────────────────────────────
// UI rendering
// ────────────────────────────────────────────────────────────────
const LEVEL_COLOR: Record<LogLevel, string> = {
  OK:   'text-[var(--status-positive)]',
  INFO: 'text-[var(--accent-secondary)]',
  DBG:  'text-[var(--text-disabled)]',
  WARN: 'text-[var(--status-warning)]',
  ERR:  'text-[var(--status-negative)]',
};

// All terminal timestamps are in Europe/Moscow (24-hour) so every
// user sees the same time regardless of their device timezone.
const MOSCOW_TIME_FMT = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatTime(ts: number): string {
  return MOSCOW_TIME_FMT.format(ts);
}

// Number of lines visible in the compact (collapsed) terminal panel.
const VISIBLE_LINES = 4;

// Считаем строку "важной" — на неё в открытой модалке делаем haptic.
// WARN/ERR — всегда. [NET] auto-claim — да (агрегированный пинг сети).
// Остальные OK/INFO/DBG — без вибрации, чтобы не дёргало раз в 3 сек.
function isImportant(line: FeedEntry): boolean {
  if (line.level === 'WARN' || line.level === 'ERR') return true;
  if (line.level === 'OK' && line.text.includes('auto-claim')) return true;
  return false;
}

import { useTranslation } from 'react-i18next';

function TerminalLogImpl() {
  const { t } = useTranslation();
  // Server-driven feed via SSE — same data for ALL connected clients.
  // `lines` приходит newest-first. Реверсим чтобы новое было ВНИЗУ
  // (как в настоящих терминалах: cat -f, journalctl -f).
  const lines = useDashboardFeed();
  const [modalOpen, setModalOpen] = useState(false);
  const visibleLines = lines.slice(0, VISIBLE_LINES);
  const reversed = [...visibleLines].reverse(); // [oldest…newest]

  return (
    <>
      {/* Compact panel — last 4 lines, click to expand. Новое — внизу. */}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="lg-pill !rounded-2xl w-full text-left px-3 py-2 font-mono text-[9px] tracking-[0.01em] text-[var(--text-muted)] flex flex-col gap-px overflow-hidden relative cursor-pointer hover:brightness-110 transition-[filter]"
        aria-label={t('nodes.terminalAria')}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {reversed.map((line, idx) => {
            // distance от самой новой (внизу): 0 = новейшая (полная),
            // больше = старее → более прозрачное (градиент уходит вверх).
            const distFromNewest = reversed.length - 1 - idx;
            const opacity = Math.max(0.28, 1 - distFromNewest * 0.2);
            return (
              <motion.div
                key={line.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                className="leading-[1.5] flex items-center gap-1.5 whitespace-nowrap overflow-hidden"
              >
                <span className="text-[var(--text-disabled)] flex-shrink-0 tabular-nums">
                  {formatTime(line.ts)}
                </span>
                <span className={`${LEVEL_COLOR[line.level]} flex-shrink-0 font-bold w-[28px]`}>
                  {line.level}
                </span>
                <span className="truncate flex-1">{line.text}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </button>

      {/* Full-history modal */}
      <TerminalLogModal
        open={modalOpen}
        lines={lines}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Modal — "real terminal" look with typewriter + blinking cursor
// ────────────────────────────────────────────────────────────────

interface TerminalLogModalProps {
  open: boolean;
  lines: FeedEntry[];
  onClose: () => void;
}

/**
 * Hook: typewriter effect — выдаёт сначала пустую строку, постепенно
 * наращивает substring до полного `text`. Скорость в ms/символ.
 * Между вызовами с одинаковым text не ре-стартует (детект по text+key).
 */
function useTypewriter(
  text: string,
  speed: number,
  enabled: boolean,
  resetKey: string | number,
): string {
  // Уважаем prefers-reduced-motion: если юзер просил минимум анимаций,
  // печатаем строку сразу без эффекта. WCAG / Apple HIG.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const effective = enabled && !prefersReducedMotion;
  const [out, setOut] = useState(effective ? '' : text);
  useEffect(() => {
    if (!effective) {
      setOut(text);
      return;
    }
    setOut('');
    if (!text) return;
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) window.clearInterval(id);
    }, speed);
    return () => window.clearInterval(id);
    // resetKey — гарантирует ре-старт при смене сообщения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, speed, effective, resetKey]);
  return out;
}

/**
 * Full-screen modal showing the entire terminal history (up to
 * MAX_HISTORY = 60 lines). Scrollable, monospace, terminal-style.
 */
function TerminalLogModal({ open, lines, onClose }: TerminalLogModalProps) {
  const uiSounds = useUISounds();
  const { haptic } = useTelegramWebApp();
  // Реверсим: oldest сверху, newest внизу — как нативный терминал.
  const reversed = useMemo(() => [...lines].reverse(), [lines]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Самый свежий id — меняется ВСЕГДА при новом событии, даже если
  // длина массива уперлась в HISTORY_LIMIT. Это и триггерит auto-scroll.
  const newestId = lines[0]?.id ?? 0;
  const newestLine = lines[0] ?? null;

  // Авто-скролл вниз. Сценарий tail-f: новые строки внизу — всегда
  // следим за ними. Если юзер сам скроллит вверх (читает историю)
  // — НЕ выдёргиваем его обратно вниз: пока scrollTop меньше
  // (scrollHeight - clientHeight - 80px) считаем что он "ушёл" и
  // молчим. Стоит ему вернуться к низу — снова автоследим.
  // requestAnimationFrame нужен потому что ref высоты ещё не
  // обновлён в момент срабатывания useEffect — реальный layout
  // случится в следующем кадре.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      const followBand = 80;
      const wasFollowing =
        el.scrollHeight - el.scrollTop - el.clientHeight <= followBand + 40;
      if (wasFollowing) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open, newestId, lines.length]);

  // Мгновенный jump вниз при первом открытии (без анимации).
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Haptic на важные новые строки. Срабатывает только когда модалка
  // открыта (юзер активно смотрит на консоль). Сравниваем по newestId
  // — на каждую новую важную пишем микровибрацию.
  const lastHapticIdRef = useRef<number>(0);
  useEffect(() => {
    if (!open) return;
    if (!newestLine) return;
    if (newestLine.id === lastHapticIdRef.current) return;
    // На самом первом открытии не вибрируем — юзер только что открыл.
    if (lastHapticIdRef.current === 0) {
      lastHapticIdRef.current = newestLine.id;
      return;
    }
    lastHapticIdRef.current = newestLine.id;
    if (!isImportant(newestLine)) return;
    if (newestLine.level === 'ERR') haptic.error();
    else if (newestLine.level === 'WARN') haptic.warning();
    else haptic.selection(); // [NET] auto-claim → light tick
  }, [open, newestLine, haptic]);

  // Reset typewriter "session" key per modal open так, чтобы при
  // повторном открытии последняя строка снова напечаталась.
  const sessionKey = open ? newestId : -1;

  // Единая точка закрытия — со звуком. Используется и кнопкой ×,
  // и backdrop-кликом, и Escape, чтобы юзер во всех случаях слышал
  // одинаковый "close" sfx.
  const closeWithSound = () => {
    uiSounds.bellClose();
    onClose();
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWithSound();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // closeWithSound стабилен в рамках одного открытия — onClose не
    // меняется. Добавлять его в deps не нужно (создаст лишние
    // реподписки).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-[max(env(safe-area-inset-top),16px)] pb-[max(env(safe-area-inset-bottom),16px)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop — лёгкий, чтобы "чёрного фона" вокруг
              модалки было меньше. Достаточно для отделения от
              дашборда, не "выпирает" сверху-снизу. */}
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={closeWithSound}
          />

          {/* Sheet — фиксированный размер, внутренний скролл.
              Гибрид: glass-рамка снаружи, чистая консоль внутри. */}
          <motion.div
            className="relative w-full max-w-md rounded-[var(--radius-card)] overflow-hidden"
            style={{
              // Компактная высота — занимает меньше экрана, чтобы
              // юзер мог одной рукой держать телефон, не растягиваясь
              // до низа модалки. Скролл работает внутри.
              height: 'min(38vh, 340px)',
              display: 'flex',
              flexDirection: 'column',
              background: '#07090f',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow:
                'var(--glass-shadow), 0 0 0 1px rgba(80, 220, 140, 0.08), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 30, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          >
            {/* Header — macOS-style 3 точки + tail-f метка. Glass-разделение */}
            <div
              className="flex items-center justify-between px-4 py-2.5 flex-shrink-0"
              style={{
                background:
                  'linear-gradient(180deg, #1a1f2b 0%, #11141c 100%)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
                </span>
                <span className="font-mono text-[10px] text-[var(--text-muted)] ml-2 tracking-[0.04em]">
                  luna@protokol: ~ {' '}
                  <span className="text-[var(--text-disabled)]">
                    tail -f system.log
                  </span>
                </span>
              </div>
              <button
                type="button"
                onClick={closeWithSound}
                className="h-12 w-12 -mr-2 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] active:scale-95 text-[32px] leading-none font-mono transition-[color,transform] duration-150"
                aria-label="Close terminal"
              >
                ×
              </button>
            </div>

            {/* Sub-header — meta about feed (events count, tz) */}
            <div
              className="px-4 py-1.5 flex-shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--text-disabled)] flex items-center gap-2"
              style={{
                background: '#0a0d14',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <span className="text-[var(--status-positive)]">●</span>
              <span>{lines.length} events</span>
              <span className="text-[var(--text-disabled)]">·</span>
              <span>europe/moscow</span>
            </div>

            {/* Scrollable list — flex-1 + min-h-0 for proper scroll.
                Reversed: oldest сверху, newest внизу. Фиксированные
                ширины колонок убирают горизонтальное "плясание". */}
            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overflow-y-auto px-3 pt-2 pb-4 font-mono text-[10.5px] leading-[1.7] text-[var(--text-secondary)]"
              style={{ background: '#07090f' }}
              onClick={(e) => e.stopPropagation()}
            >
              {reversed.map((line, idx) => {
                const isLast = idx === reversed.length - 1;
                return (
                  <TerminalLine
                    key={line.id}
                    line={line}
                    isLast={isLast}
                    sessionKey={sessionKey}
                  />
                );
              })}
            </div>

            {/* Footer hint */}
            <div
              className="px-4 py-2 flex-shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--text-disabled)] text-center"
              style={{
                background: '#0a0d14',
                borderTop: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              tap outside · esc to exit
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ────────────────────────────────────────────────────────────────
// Single terminal line — fixed columns, optional typewriter + cursor
// ────────────────────────────────────────────────────────────────

function TerminalLine({
  line,
  isLast,
  sessionKey,
}: {
  line: FeedEntry;
  isLast: boolean;
  sessionKey: number;
}) {
  // Typewriter только для самой свежей строки в текущей session.
  // Остальные — сразу полный текст. Скорость 14 ms/симв даёт ~0.7 c
  // на 50-символьной строке.
  const text = useTypewriter(line.text, 14, isLast, sessionKey);
  const typing = isLast && text.length < line.text.length;
  return (
    <div className="flex items-baseline gap-1.5 whitespace-pre">
      <span className="text-[var(--text-disabled)] tabular-nums flex-shrink-0">
        {formatTime(line.ts)}
      </span>
      <span
        className={`${LEVEL_COLOR[line.level]} font-bold w-[30px] flex-shrink-0 text-left`}
      >
        {line.level}
      </span>
      <span className="text-[var(--text-secondary)] flex-1 break-all whitespace-pre-wrap">
        {text}
        {/* Курсор — мигает на последней строке. Когда строка ещё
            печатается — курсор сразу за символом (видно прогресс).
            Когда уже допечатана — мигает в конце как prompt. */}
        {isLast && (
          <BlinkingCursor solid={typing} />
        )}
      </span>
    </div>
  );
}

function BlinkingCursor({ solid = false }: { solid?: boolean }) {
  // Когда строка ещё печатается (solid=true) — курсор не мигает,
  // он просто "тянется" за последним символом. Когда допечатано —
  // мигает как настоящий terminal prompt (1.06 сек цикл — каноничный
  // ритм терминала).
  const baseStyle = {
    width: '7px',
    height: '12px',
    background: 'var(--status-positive)',
    verticalAlign: 'text-bottom',
    boxShadow: '0 0 6px var(--status-positive)',
  } as const;
  if (solid) {
    return (
      <span
        aria-hidden="true"
        className="inline-block ml-0.5"
        style={baseStyle}
      />
    );
  }
  return (
    <motion.span
      aria-hidden="true"
      className="inline-block ml-0.5"
      style={baseStyle}
      animate={{ opacity: [1, 1, 0, 0] }}
      transition={{ duration: 1.06, times: [0, 0.5, 0.5, 1], repeat: Infinity, ease: 'linear' }}
    />
  );
}

export const TerminalLog = memo(TerminalLogImpl);
